import { MouseEvent, useCallback } from 'react';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Link as RouterLink } from 'react-router';
import { css } from '@emotion/react';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { ISourcingCompanyRow } from '../../../schemas/SourcingListItems.schema';
import { colors } from '../../../theme/colors';

// Grid-friendly link: inherits cell typography and underlines only on hover.
// stopPropagation prevents the grid from treating the click as a row selection
// or as an expand toggle on the master-detail chevron.
const linkCss = css`
  color: ${colors.primary[60]};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Picks the most reliable identifier for the profile route, in priority order:
 *   1. `domain`       → clean, readable slug that the profile-page resolver
 *                       will map to `domain` query param.
 *   2. `linkedinUrl`  → resolver maps `linkedin.com/company/...` URLs to the
 *                       `linkedinUrl` lookup.
 *   3. `id`           → last resort; may be a Crunchbase UUID or Harmonic URN,
 *                       both of which the resolver understands.
 * Returns `null` when none of these are usable (the link is then not rendered).
 */
function pickProfileIdentifier(row: ISourcingCompanyRow): string | null {
  if (row.domain && row.domain.trim().length > 0) {
    return row.domain.trim();
  }
  if (row.linkedinUrl && row.linkedinUrl.trim().length > 0) {
    return row.linkedinUrl.trim();
  }
  if (row.id && row.id.trim().length > 0) {
    return row.id.trim();
  }
  return null;
}

export function SourcingCompanyNameLink(params: CustomCellRendererProps<ISourcingCompanyRow>) {
  const handleClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  }, []);

  const displayValue = params.valueFormatted ?? params.value ?? '';
  const row = params.data;

  if (!row) {
    return <>{displayValue}</>;
  }

  const identifier = pickProfileIdentifier(row);
  if (!identifier) {
    return <>{displayValue}</>;
  }

  const to = `/${ROUTES.SOURCING}/${ROUTES.SOURCING_PROFILE}/${encodeURIComponent(identifier)}`;

  return (
    <RouterLink css={linkCss} to={to} onClick={handleClick} target='_blank' rel='noopener noreferrer'>
      {displayValue}
    </RouterLink>
  );
}
